import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { toJS } from 'mobx';
import { cast, getParent, types } from 'mobx-state-tree';
import moment from 'moment';
import { api } from 'src/api';
import { TRANSACTION_TYPESV2 } from 'src/constants/payments';
import { createAsync } from 'src/utils/asyncModel';
const getIds = response => response === null || response === void 0 ? void 0 : response.response.reduce((acc, item) => {
  acc.push(item.accountId);
  return acc;
}, []);
const getAccountStatementsPreviewAsync = createAsync(data => {
  return async function getAccountStatementsPreview(options) {
    const resp = await api.Statement.getAccountStatementsPreview(data, options);
    const _getParent = getParent(this),
      statement = _getParent.statement;
    statement.setAccountStatementsPreviewAfterFetch(resp);
    return resp;
  };
});
const getPropertyStatementsAsync = createAsync(body => {
  return async function getPropertyStatements(options) {
    const resp = await api.Statement.getPropertyStatements(body, options);
    const _getParent2 = getParent(this),
      account = _getParent2.account,
      statement = _getParent2.statement;
    statement.setPropertyStatementsAfterFetch(resp, body.month, body.year, body.propertyId);
    await account.getUsersProfilesAsync.run({
      accountIds: getIds(resp)
    });
    return resp;
  };
});
const sendPaperStatementAsync = createAsync(body => {
  return async function sendPaperStatement(options) {
    const resp = await api.Statement.sendPaperStatement(body, options);
    return resp;
  };
});
const getStatementAsync = createAsync(statementId => {
  return async function getStatement(options) {
    const resp = await api.Statement.statement(statementId, options);
    const _getParent3 = getParent(this),
      statement = _getParent3.statement;
    statement.assignStatementsAfterLoad(resp, statementId);
    return resp;
  };
});
const voidStatementAsync = createAsync(body => {
  return async function voidStatement(options) {
    const resp = await api.Statement.voidStatement(body, options);
    return resp;
  };
});
const AccountStatementsPreview = types.model('AccountStatementsPreview', {
  accountId: types.string,
  isFinalStatement: types.boolean,
  isRegularStatement: types.boolean,
  isVoided: types.boolean,
  month: types.number,
  statementId: types.string,
  timestamp: types.string,
  total: types.number,
  year: types.number,
  ym: types.number
});
const Statement = types.model('Statement', {
  accountId: types.string,
  chargesTotal: types.number,
  companyId: types.string,
  creditsTotal: types.number,
  fileId: types.string,
  id: types.string,
  month: types.number,
  propertyId: types.string,
  residencyId: types.string,
  statementCode: types.string,
  timestamp: types.string,
  total: types.number,
  unitId: types.string,
  unitName: types.maybeNull(types.string),
  utilityBillsTotal: types.number,
  utilityCapsTotal: types.number,
  statementCapsTotal: types.number,
  year: types.number
});
const StatementCharges = types.model('StatementCharges', {
  amount: types.number,
  billingCycleId: types.maybeNull(types.string),
  endDateOnly: types.maybeNull(types.string),
  financialTransactionId: types.maybeNull(types.string),
  isCredit: types.maybeNull(types.boolean),
  isSkipped: types.boolean,
  isOptOut: types.boolean,
  metadata: types.maybeNull(types.frozen()),
  startDateOnly: types.maybeNull(types.string),
  submeterMeters: types.maybeNull(types.frozen()),
  type: types.string,
  utilityId: types.maybeNull(types.string)
});
const StatementData = types.model('StatementData', {
  atLeastOneCharge: types.boolean,
  charges: types.optional(types.array(StatementCharges), []),
  fileId: types.string,
  id: types.string,
  isVoided: types.boolean,
  paperFileId: types.maybeNull(types.string),
  // The property the statement belongs to, which is not the page's for a statement from an earlier residency.
  propertyId: types.maybeNull(types.string),
  submeterDisclosures: types.maybeNull(types.frozen()),
  total: types.number,
  voidedTransactionIds: types.array(types.string)
});
const StatementStore = types.model('StatementStore', {
  accountStatementsPreviewValue: types.optional(types.array(AccountStatementsPreview), []),
  getAccountStatementsPreviewAsync,
  getPropertyStatementsAsync,
  getStatementAsync,
  propertyStatementsValue: types.optional(types.array(Statement), []),
  propertyStatementsByMonthValue: types.map(types.array(Statement)),
  sendPaperStatementAsync,
  statementValue: types.map(types.maybe(StatementData)),
  voidStatementAsync,
  voidedTransactionDataValue: types.map(types.maybe(types.frozen()))
}).actions(self => ({
  assignStatementsAfterLoad(data, statementId) {
    try {
      var _data$response, _data$response2, _data$response3, _data$response4, _data$response5, _data$response6, _data$response7, _data$response8, _data$response$proper, _data$response9, _data$response$submet, _data$response0;
      const atLeastOneCharge = data.response.delayedCharges.charges.length || data.response.otherCharges.charges.length || data.response.statementCharges.items.length || data.response.statementCredits.items.length || data.response.utilityCharges.charges.length;
      const voidedTransactionIds = [];
      data === null || data === void 0 || (_data$response = data.response) === null || _data$response === void 0 || (_data$response = _data$response.statementCharges) === null || _data$response === void 0 || _data$response.items.map(i => {
        if (i.type === TRANSACTION_TYPESV2.Void) {
          var _i$metadata;
          voidedTransactionIds.push(i === null || i === void 0 || (_i$metadata = i.metadata) === null || _i$metadata === void 0 ? void 0 : _i$metadata.voidedTransactionId);
        }
      }).filter(Boolean);
      data === null || data === void 0 || (_data$response2 = data.response) === null || _data$response2 === void 0 || (_data$response2 = _data$response2.statementCredits) === null || _data$response2 === void 0 || _data$response2.items.map(i => {
        if (i.type === TRANSACTION_TYPESV2.Void) {
          var _i$metadata2;
          voidedTransactionIds.push(i === null || i === void 0 || (_i$metadata2 = i.metadata) === null || _i$metadata2 === void 0 ? void 0 : _i$metadata2.voidedTransactionId);
        }
      }).filter(Boolean);
      self.statementValue.set(statementId, {
        atLeastOneCharge: Boolean(atLeastOneCharge),
        charges: cast([...(data === null || data === void 0 || (_data$response3 = data.response) === null || _data$response3 === void 0 || (_data$response3 = _data$response3.statementCharges) === null || _data$response3 === void 0 ? void 0 : _data$response3.items), ...(data === null || data === void 0 || (_data$response4 = data.response) === null || _data$response4 === void 0 || (_data$response4 = _data$response4.statementCredits) === null || _data$response4 === void 0 ? void 0 : _data$response4.items.map(i => _objectSpread(_objectSpread({}, i), {}, {
          isCredit: true
        })))]),
        fileId: data === null || data === void 0 || (_data$response5 = data.response) === null || _data$response5 === void 0 ? void 0 : _data$response5.fileId,
        id: data === null || data === void 0 || (_data$response6 = data.response) === null || _data$response6 === void 0 ? void 0 : _data$response6.id,
        isVoided: data === null || data === void 0 || (_data$response7 = data.response) === null || _data$response7 === void 0 ? void 0 : _data$response7.isVoided,
        paperFileId: data === null || data === void 0 || (_data$response8 = data.response) === null || _data$response8 === void 0 ? void 0 : _data$response8.paperFileId,
        propertyId: (_data$response$proper = data === null || data === void 0 || (_data$response9 = data.response) === null || _data$response9 === void 0 ? void 0 : _data$response9.propertyId) !== null && _data$response$proper !== void 0 ? _data$response$proper : null,
        submeterDisclosures: (_data$response$submet = data === null || data === void 0 || (_data$response0 = data.response) === null || _data$response0 === void 0 ? void 0 : _data$response0.submeterDisclosures) !== null && _data$response$submet !== void 0 ? _data$response$submet : null,
        total: data.response.total,
        voidedTransactionIds
      });
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  assignVoidedTransactionDataAfterLoad(data) {
    try {
      data.forEach(d => self.voidedTransactionDataValue.set(d.id, d));
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  setAccountStatementsPreviewAfterFetch(resp) {
    try {
      const statements = resp.response.map(s => _objectSpread(_objectSpread({}, s), {}, {
        ym: Number("".concat(s.year).concat(s.month.toString().padStart(2, '0')))
      })).sort((a, b) => moment(a.timestamp).isBefore(moment(b.timestamp)) ? 1 : -1);
      self.accountStatementsPreviewValue = cast(statements);
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  },
  setPropertyStatementsAfterFetch(resp, month, year, propertyId) {
    try {
      const sortedResp = resp.response.sort((a, b) => {
        if (a.unitName < b.unitName) {
          return -1;
        }
        if (a.unitName > b.unitName) {
          return 1;
        }
        return 0;
      });
      self.propertyStatementsValue = cast(sortedResp);
      self.propertyStatementsByMonthValue.set(propertyId + '_' + month + '_' + year, cast(sortedResp));
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  }
})).views(self => ({
  get accountStatementsPreview() {
    return toJS(self.accountStatementsPreviewValue);
  },
  get propertyStatements() {
    return self.propertyStatementsValue;
  },
  get propertyStatementsByMonth() {
    return self.propertyStatementsByMonthValue;
  },
  statementData(statementId) {
    return toJS(self.statementValue.get(statementId));
  },
  voidedTransactionData(transactionId) {
    return toJS(self.voidedTransactionDataValue.get(transactionId));
  }
}));
export const statement = types.optional(StatementStore, {});